import { componentText, SchmancyElement } from '@schmancy/mixins/index'
import dayjs from 'dayjs'
import { css, html, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
// Registering side-effect imports — this dialog composes all three tags, so
// it must not depend on a consumer's barrel import order to upgrade them.
import '@schmancy/primitives/calendar/calendar'
import '@schmancy/form/fields/chips/chips'
import '@schmancy/form/fields/chips/filter-chip'
import type { SchmancyCalendarChangeEvent, SchmancyCalendarInputEvent } from '@schmancy/primitives/calendar/calendar'
import '../input/input'

export interface DateRangePreset {
	id: string
	label: string
	start: string
	end: string
}

/**
 * Picker panel for `<schmancy-date-range>`.
 *
 * Layout: sticky chip quick-rail → continuous-scroll calendar → secondary typed path.
 * Emits composed events that `<schmancy-date-range>` bridges into its own
 * `input`/`change` events.
 *
 * @element schmancy-date-range-dialog
 * @fires calendar-input  - Provisional sweep from the calendar   { start, end }
 * @fires calendar-change - Committed endpoint from the calendar   { start, end }
 * @fires preset-select   - A chip was clicked                     { id, label, start, end }
 */
@customElement('schmancy-date-range-dialog')
export class SchmancyDateRangeDialog extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	@property() type: 'date' | 'datetime-local' = 'date'
	@property() start: string | null = null
	@property() end: string | null = null
	@property() min?: string
	@property() max?: string
	@property() preset: string | null = null
	@property({ type: Array }) presets: DateRangePreset[] = []

	private handleCalendarInput(e: SchmancyCalendarInputEvent) {
		e.stopPropagation()
		this.dispatchEvent(
			new CustomEvent('calendar-input', {
				detail: { start: e.detail.start, end: e.detail.end },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleCalendarChange(e: SchmancyCalendarChangeEvent) {
		e.stopPropagation()
		// When type="datetime-local", the calendar emits YYYY-MM-DD date strings.
		// A calendar-only pick commits start-of-day (T00:00) for the start and
		// end-of-day (T23:59) for the end, since the calendar has no time UI —
		// this is the only value the missing time segment can default to.
		const isDatetime = this.type === 'datetime-local'
		const start =
			isDatetime && e.detail.start && !e.detail.start.includes('T') ? `${e.detail.start}T00:00` : e.detail.start
		const end = isDatetime && e.detail.end && !e.detail.end.includes('T') ? `${e.detail.end}T23:59` : e.detail.end
		// The parent's `.start`/`.end` bindings are captured once when the panel
		// opens and never re-assigned during the session (the overlay only
		// re-renders its mounted content on a Signal read, and these are plain
		// properties) — so the footer's From/To fields must track the commit
		// on this element directly, or they'd keep showing whatever was open
		// when the panel first mounted.
		this.start = start
		this.end = end
		this.dispatchEvent(
			new CustomEvent('calendar-change', {
				detail: { start, end },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handlePresetChange(e: CustomEvent<{ value: string | string[] }>) {
		e.stopPropagation()
		const id = Array.isArray(e.detail.value) ? e.detail.value[0] : e.detail.value
		if (!id) return
		const preset = this.presets.find(p => p.id === id)
		if (!preset) return
		this.start = preset.start
		this.end = preset.end
		this.preset = preset.id
		this.dispatchEvent(
			new CustomEvent('preset-select', {
				detail: preset,
				bubbles: true,
				composed: true,
			}),
		)
	}

	/** Live-typed From/To preview — mirrors `handleCalendarInput`'s provisional-only
	 * contract (never committed to `this.start`/`this.end`) so the parent's `input`
	 * event fires on every keystroke, the same as `<schmancy-input>` itself already
	 * does and the same as a native `<input>` does; only `handleFromChange`/
	 * `handleToChange` (on blur/commit) write the value. */
	private handleFromInput(e: CustomEvent<{ value: string }>) {
		e.stopPropagation()
		const value = e.detail.value
		if (!value) return
		this.dispatchEvent(
			new CustomEvent('calendar-input', {
				detail: { start: value, end: this.end },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleToInput(e: CustomEvent<{ value: string }>) {
		e.stopPropagation()
		const value = e.detail.value
		if (!value) return
		this.dispatchEvent(
			new CustomEvent('calendar-input', {
				detail: { start: this.start, end: value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleFromChange(e: Event) {
		const value = (e.target as HTMLInputElement).value
		if (!value) return
		const newStart = value
		const newEnd = this.end && dayjs(this.end).isBefore(dayjs(newStart)) ? newStart : (this.end ?? null)
		this.start = newStart
		this.end = newEnd
		this.dispatchEvent(
			new CustomEvent('calendar-change', {
				detail: { start: newStart, end: newEnd },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleToChange(e: Event) {
		const value = (e.target as HTMLInputElement).value
		if (!value) return
		const newEnd = value
		const newStart = this.start && dayjs(this.start).isAfter(dayjs(newEnd)) ? newEnd : (this.start ?? null)
		this.start = newStart
		this.end = newEnd
		this.dispatchEvent(
			new CustomEvent('calendar-change', {
				detail: { start: newStart, end: newEnd },
				bubbles: true,
				composed: true,
			}),
		)
	}

	render() {
		// Native `datetime-local` renders a date segment AND a time segment
		// (incl. AM/PM) in one row — measured at ~184px intrinsic content width
		// at this control's md size/font (`date` alone measures ~141px). Two of
		// those side by side never fit the panel's ~280–360px width budget
		// without clipping the time segment, so datetime-local stacks the
		// From/To fields into one column instead of splitting the row in two.
		const isDatetime = this.type === 'datetime-local'
		return html`
			<!-- mx-auto for the reason date-field-dialog carries it: the overlay
			     mounts content into a grid-template-columns: minmax(0,1fr) area, and
			     a grid item whose max-width stops it filling that area falls back
			     from stretch to start, pinning this panel to the left edge of any
			     surface wider than 90. -->
			<div class="flex flex-col w-full min-w-70 max-w-90 mx-auto">
				${when(
					this.presets.length > 0,
					() => html`
						<!-- py-3, not p-3: eight presets never fit this panel's 280-360px width, and
						     horizontal padding out here would shrink the chip row's own scroll viewport
						     rather than pad it — the row would then stop 12px short of the bar on both
						     sides and read as a boxed cut. inline-padding puts the same 12px inside the
						     scroller, so the row runs to the bar's real edges and only the first and
						     last preset rest inside it. -->
						<div part="presets" class="sticky top-0 z-20 bg-surface-default py-3 border-b border-outline-variant/40">
							<schmancy-chips
								class="w-full"
								.label=${componentText('dateRange.presets')}
								inline-padding="px-3"
								.value=${this.preset ?? ''}
								@change=${this.handlePresetChange}
							>
								${repeat(
									this.presets,
									p => p.id,
									p => html`
										<schmancy-filter-chip .value=${p.id} title="${p.start} — ${p.end}">
											${p.label}
										</schmancy-filter-chip>
									`,
								)}
							</schmancy-chips>
						</div>
					`,
					() => nothing,
				)}

				<!-- ONE scrollport, the calendar's own — the same collapse
				     schmancy-date-field-dialog carries, and for the same measured
				     reason. The calendar already scrolls inside a host that declares
				     its own height; a wrapper adding overflow-y-auto and a max-h-80 of
				     its own made two elements each answer "how tall is the calendar",
				     352 and 320, and the outer one clipped 32px off the inner one it
				     could not see. Measured live here at 1280x900 before the collapse:
				     box 320, content 352, overflow 32.
				     The calendar disables a day only when no instant within it falls in
				     [min, max] (its own day-vs-instant comparison), so the full min/max —
				     datetime-local time-of-day included — passes through unmodified; the
				     precise instant is still enforced by checkValidity's
				     rangeUnderflow/rangeOverflow. -->
				<schmancy-calendar
					part="calendar"
					class="max-h-80"
					mode="range"
					.type=${this.type}
					.start=${this.start}
					.end=${this.end}
					min=${ifDefined(this.min)}
					max=${ifDefined(this.max)}
					@input=${(e: SchmancyCalendarInputEvent) => this.handleCalendarInput(e)}
					@change=${(e: SchmancyCalendarChangeEvent) => this.handleCalendarChange(e)}
				></schmancy-calendar>

				<div
					part="footer"
					class=${this.classMap({
						'flex gap-2 p-3 border-t border-outline-variant/40': true,
						'flex-col': isDatetime,
						'flex-wrap': !isDatetime,
					})}
				>
					<div class=${this.classMap({ 'w-full': isDatetime, 'flex-1 min-w-30': !isDatetime })}>
						<schmancy-input
							.type=${this.type}
							.label=${componentText('dateRange.from')}
							.value=${this.start ?? ''}
							min=${ifDefined(this.min)}
							max=${ifDefined(this.max)}
							@input=${this.handleFromInput}
							@change=${this.handleFromChange}
						></schmancy-input>
					</div>
					<div class=${this.classMap({ 'w-full': isDatetime, 'flex-1 min-w-30': !isDatetime })}>
						<schmancy-input
							.type=${this.type}
							.label=${componentText('dateRange.to')}
							.value=${this.end ?? ''}
							min=${ifDefined(this.start ?? this.min)}
							max=${ifDefined(this.max)}
							@input=${this.handleToInput}
							@change=${this.handleToChange}
						></schmancy-input>
					</div>
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-date-range-dialog': SchmancyDateRangeDialog
	}
}
